import { noop } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Filter } from '~/evifields/src/types/Filter';
import { QueryFilters } from '~/features/advanced-search/query-filters/QueryFilters';

import { QueryFiltersProps } from '../types';

type FormFieldMode = 'preview' | 'edit';

interface Props extends Omit<QueryFiltersProps, 'mode'> {
  title: string;
  description?: string;
  descriptionLink?: {
    linkText: string;
    pathname: string;
    additionalText?: string;
    tooltip?: string;
  };
  mode?: FormFieldMode;
  optional?: boolean;
  required?: boolean;
  info?: string;
  onSaveFilters?: () => void;
  onCancel?: () => void;
}

export const QueryFiltersFormField = ({
  defaultFilters,
  description,
  descriptionLink,
  enableQueryBuilder,
  mode,
  query,
  info,
  restrictedFilterIds,
  optional = true,
  required = false,
  readOnly = false,
  title,
  onQueryChange,
  onSaveFilters,
  onCancel,
}: Props) => {
  const [isEditing, _toggleIsEditing, enableIsEditing, disableIsEditing] =
    useToggle();

  const intl = useIntl();

  const isEditMode = mode === 'edit';

  return (
    <FormField<Filter[], true>
      actions={
        isEditMode
          ? !isEditing
            ? [
                {
                  icon: 'edit',
                  text: intl.formatMessage({
                    id: 'QueryFiltersFormField.editFiltersButton',
                    defaultMessage: 'Edit Filters',
                    description: 'Button text to edit filters',
                  }),
                  level: 'action',
                  onClick: enableIsEditing,
                },
              ]
            : [
                {
                  icon: 'x',
                  text: intl.formatMessage({
                    id: 'QueryFiltersFormField.cancelButton',
                    defaultMessage: 'Cancel',
                    description: 'Button text to cancel editing',
                  }),
                  level: 'action',
                  onClick: () => {
                    onCancel?.();
                    disableIsEditing();
                  },
                },
                {
                  icon: 'check',
                  text: intl.formatMessage({
                    id: 'QueryFiltersFormField.saveChangesButton',
                    defaultMessage: 'Save Changes',
                    description: 'Button text to save changes',
                  }),
                  level: 'action',
                  onClick: () => {
                    onSaveFilters?.();
                    disableIsEditing();
                  },
                },
              ]
          : []
      }
      required={required}
      description={description}
      descriptionLink={descriptionLink}
      input={null}
      label={title}
      name="search-filters-preview"
      onChange={noop}
      optional={optional}
      value={null}
      info={info}
    >
      <Layout pt={2}>
        <QueryFilters
          mode={isEditMode ? undefined : mode}
          defaultFilters={defaultFilters}
          enableQueryBuilder={enableQueryBuilder}
          restrictedFilterIds={restrictedFilterIds}
          onQueryChange={onQueryChange}
          query={query}
          readOnly={readOnly || (isEditMode && !isEditing)}
        />
      </Layout>
    </FormField>
  );
};
